import "@/App.css";
import { useLayoutEffect, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
// AnimatePresence kept for potential nested usage; RouteCurtain uses key remounts.
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { initMeta, metaPageView } from "@/lib/meta";
import { Layout } from "@/components/layout/Layout";
import { IntroLoader } from "@/components/IntroLoader";
import { SmoothScrollProvider, useLenisRef } from "@/motion/SmoothScroll";
import { ScrollTrigger } from "@/motion/gsapSetup";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import CheckoutConfirm from "@/pages/CheckoutConfirm";
import RecoveryLanding from "@/pages/RecoveryLanding";
import Unsubscribe from "@/pages/Unsubscribe";
import TradeIn from "@/pages/TradeIn";
import ServicePage from "@/pages/ServicePage";
import NotFound from "@/pages/NotFound";
import Offers from "@/pages/Offers";
import AdminApp from "@/admin/AdminApp";

// Riporta in alto la nuova pagina prima del paint (via Lenis se attivo).
function ScrollToTop() {
  const lenisRef = useLenisRef();
  useLayoutEffect(() => {
    const lenis = lenisRef?.current;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    // Ricalcola i trigger dopo il cambio pagina.
    const t = setTimeout(() => ScrollTrigger.refresh(), 200);
    return () => clearTimeout(t);
  }, [lenisRef]);
  return null;
}

// Transizione tra pagine: tendina #0A0A0B che sale, si monta la pagina, esce verso l'alto.
function RouteCurtain() {
  const location = useLocation();
  return (
    <motion.div
      key={location.pathname}
      className="pointer-events-none fixed inset-0 z-[300] bg-ink"
      initial={{ y: "100%" }}
      animate={{ y: ["100%", "0%", "0%", "-100%"] }}
      transition={{ duration: 0.9, times: [0, 0.46, 0.52, 1], ease: [0.7, 0, 0.84, 0] }}
      aria-hidden="true"
    />
  );
}

// Contenuto pagina: fade breve (la tendina copre lo swap).
function AnimatedRoutes() {
  const location = useLocation();
  const firstRef = useRef(true);
  useEffect(() => { initMeta(); }, []);
  useEffect(() => {
    // Il pixel invia già il PageView iniziale al caricamento; qui tracciamo i
    // cambi pagina SPA successivi (browser + server, stesso event_id).
    if (firstRef.current) { firstRef.current = false; return; }
    metaPageView();
  }, [location.pathname]);
  return (
    <>
      <RouteCurtain />
      <motion.div
        key={location.pathname}
        className="flex flex-1 flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1], delay: 0.28 }}
        data-testid="page-transition"
      >
        <ScrollToTop />
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/catalogo" element={<Catalog />} />
          <Route path="/offerte" element={<Offers />} />
          <Route path="/catalogo/:brand" element={<Catalog />} />
          <Route path="/marchi" element={<Navigate to="/catalogo" replace />} />
          <Route path="/marchi/:brand" element={<Navigate to="/catalogo" replace />} />
          <Route path="/prodotto/:slug" element={<ProductDetail />} />
          <Route path="/carrello" element={<Cart />} />
          <Route path="/carrello/recupero/:token" element={<RecoveryLanding />} />
          <Route path="/disiscrizione/:token" element={<Unsubscribe />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/ordine/conferma" element={<CheckoutConfirm />} />
          <Route path="/ordine/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/valuta" element={<TradeIn />} />
          <Route path="/chi-siamo" element={<ServicePage slug="chi-siamo" />} />
          <Route path="/spedizioni-e-resi" element={<ServicePage slug="spedizioni-e-resi" />} />
          <Route path="/garanzia" element={<ServicePage slug="garanzia" />} />
          <Route path="/faq" element={<ServicePage slug="faq" />} />
          <Route path="/contatti" element={<ServicePage slug="contatti" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </>
  );
}

function MarketingApp() {
  return (
    <div className="App grain">
      <MotionConfig reducedMotion="user">
        <IntroLoader />
        <SmoothScrollProvider>
          <CartProvider>
            <Layout>
              <AnimatedRoutes />
            </Layout>
            <Toaster position="bottom-right" />
          </CartProvider>
        </SmoothScrollProvider>
      </MotionConfig>
    </div>
  );
}

// Sceglie il "mondo" da montare: /admin \u00e8 isolato dallo storefront
// (niente smooth-scroll, tendine di transizione o grain del marketing).
function RootSwitch() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) {
    return <AdminApp />;
  }
  return <MarketingApp />;
}

function App() {
  return (
    <BrowserRouter>
      <RootSwitch />
    </BrowserRouter>
  );
}

export default App;
